import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {StyleSheet, View} from 'react-native';
import {zodResolver} from '@hookform/resolvers/zod';

import {Address} from '../../../models/middleware';
import {
  ManualAddressFieldsFormData,
  manualAddressFieldsSchema,
} from '../../../schemas/checkoutSchemas';
import Button from '../../themed/Button';
import Text from '../../themed/Text';
import TextInput from '../../themed/TextInput';
import ValidationMessage from '../form/ValidationMessage';

import SuburbPicker from './SuburbPicker';

function InputLabel({children, required}: {children: React.ReactNode; required?: boolean}) {
  return (
    <Text variant="body2" style={styles.inputLabel}>
      {children}
      {!!required && (
        <Text variant="caption" style={styles.requiredAsterisk} accessibilityLabel="required field">
          {' *'}
        </Text>
      )}
    </Text>
  );
}

type SuburbFields = Pick<Address, 'locality' | 'postal_code' | 'administrative_area'>;

type Props = {
  onChange: (address: Address) => void;
  testID?: string;
  /** Button text override */
  submitButtonText?: string;
  /**
   * DIS-1592: pre-select the suburb when the caller has already collected it, so the
   * user does not re-enter it. Street lines always start empty. Omit for a fresh form.
   */
  initialSuburb?: SuburbFields;
};

const ManualAddressEntry = ({
  onChange,
  testID,
  submitButtonText = 'Use this address',
  initialSuburb,
}: Props) => {
  const suburbFields: SuburbFields = {
    locality: initialSuburb?.locality ?? '',
    postal_code: initialSuburb?.postal_code ?? '',
    administrative_area: initialSuburb?.administrative_area ?? '',
  };

  // Street lines always start empty — only the suburb can be carried in.
  const defaultValues: ManualAddressFieldsFormData = {
    line1: '',
    line2: '',
    ...suburbFields,
  };

  const form = useForm<ManualAddressFieldsFormData>({
    resolver: zodResolver(manualAddressFieldsSchema),
    defaultValues,
    mode: 'onTouched',
  });

  const onSubmit = (enteredAddress: ManualAddressFieldsFormData) => {
    const lines = [];

    if (enteredAddress.line1 && enteredAddress.line1.trim() !== '') {
      lines.push(enteredAddress.line1);
    }

    if (enteredAddress.line2 && enteredAddress.line2.trim() !== '') {
      lines.push(enteredAddress.line2);
    }

    onChange({
      lines,
      postal_code: enteredAddress.postal_code,
      locality: enteredAddress.locality,
      administrative_area: enteredAddress.administrative_area,
    });
  };

  return (
    <View style={styles.container}>
      {/* Helper text */}
      <Text style={styles.helperText} variant="caption">
        Enter your address details below
      </Text>

      {/* Form fields - MFL format: lines[0]=unit, lines[1]=street */}
      <View style={styles.form}>
        <Controller
          control={form.control}
          name="line1"
          render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
            <TextInput
              label={<InputLabel>Unit/Apartment (optional)</InputLabel>}
              autoCapitalize="words"
              autoComplete="off"
              value={value || ''}
              errorMessageWhenTouched={error?.message}
              onBlur={onBlur}
              onChangeText={onChange}
              placeholder="e.g. Unit 4, Level 2"
              touched={isTouched || form.formState.isSubmitted}
              testID={testID ? `${testID}-line1` : undefined}
            />
          )}
        />

        <Controller
          control={form.control}
          name="line2"
          render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
            <TextInput
              label={<InputLabel required>Street address</InputLabel>}
              autoCapitalize="words"
              autoComplete="street-address"
              value={value || ''}
              errorMessageWhenTouched={error?.message}
              onBlur={onBlur}
              onChangeText={onChange}
              placeholder="e.g. 123 Main Street"
              touched={isTouched || form.formState.isSubmitted}
              testID={testID ? `${testID}-line2` : undefined}
            />
          )}
        />

        <View style={styles.suburbField}>
          <InputLabel required>Suburb</InputLabel>
          <SuburbPicker
            initialSuburb={suburbFields}
            onSelectSuburb={suburb => {
              form.setValue('locality', suburb.locality, {shouldTouch: true});
              form.setValue('postal_code', suburb.postal_code, {shouldTouch: true});
              form.setValue('administrative_area', suburb.administrative_area, {shouldTouch: true});
            }}
            testID={testID ? `${testID}-suburb` : undefined}
          />
          {!!(
            form.formState.errors.locality ||
            form.formState.errors.postal_code ||
            form.formState.errors.administrative_area
          ) &&
          (form.formState.isSubmitted ||
            form.formState.touchedFields.locality ||
            form.formState.touchedFields.postal_code ||
            form.formState.touchedFields.administrative_area) ? (
            <ValidationMessage>Please select a suburb</ValidationMessage>
          ) : null}
        </View>
      </View>

      {/* Action button */}
      <View style={styles.buttons}>
        <Button
          onPress={form.handleSubmit(onSubmit)}
          title={submitButtonText}
          testID={testID ? `${testID}-submit` : undefined}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    // No flex: 1 - let content determine height (like PersonAcceptingTheOrder)
  },
  helperText: {
    // TODO: added to theme token after centralized theming implementation
    color: '#6B7280',
    fontStyle: 'italic',
    marginBottom: 16,
  },
  form: {
    // No flex: 1 - prevents form from expanding and pushing button off-screen
    gap: 12,
  },
  inputLabel: {
    marginBottom: 5,
  },
  requiredAsterisk: {
    fontSize: 14,
    // TODO: added to theme token after centralized theming implementation
    color: '#ef4444',
    marginLeft: 2,
    lineHeight: 14,
  },
  suburbField: {
    marginTop: 8,
  },
  buttons: {
    marginTop: 24,
    marginBottom: 16,
  },
});

export default ManualAddressEntry;
